import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, errorMessageKey } from '../../api/api-error';
import { paths } from '../../app/paths';
import { Brand } from '../../components/brand';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Notice } from '../../components/ui/notice';
import { UserMenu } from '../../components/user-menu';
import { diagramsKey, useDiagrams } from '../../diagrams/diagrams-api';
import { ChatStart } from '../../home/chat-start';
import { DiagramFormDialog } from '../../home/diagram-form-dialog';
import { DiagramList, type ListedDiagram } from '../../home/diagram-list';
import { homeCommands } from '../../home/home-commands';
import { useI18n } from '../../i18n/i18n';
import { ArchivedNotice } from '../../workspaces/archived-notice';
import { useCurrentWorkspace } from '../../workspaces/current-workspace';
import { can } from '../../workspaces/workspaces-api';

type Dialog =
  | { kind: 'create' }
  | { kind: 'rename'; diagram: ListedDiagram }
  | { kind: 'delete'; diagram: ListedDiagram };

/**
 * Домашний экран пространства (docs/saas/screens/блок-схемы.md): знак слева вверху, иконка
 * пользователя справа, чат-старт по центру, ниже — блок-схемы свежеизменёнными сверху.
 */
export function HomeScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspace = useCurrentWorkspace();
  const diagrams = useDiagrams(workspace.id);
  const commands = useMemo(() => homeCommands(workspace.id), [workspace.id]);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const items: ListedDiagram[] = diagrams.data?.pages.flatMap((page) => page.items) ?? [];
  const writable = !workspace.archived;
  const canCreate = can(workspace, 'diagram.create') && writable;
  const refresh = () => queryClient.invalidateQueries({ queryKey: diagramsKey(workspace.id) });

  // Курсор от прежнего порядка списка (после выкладки) — начать список сначала (ADR-0018).
  const cursorStale =
    diagrams.error instanceof ApiError && diagrams.error.code === 'CURSOR_INVALID';
  useEffect(() => {
    if (cursorStale) void queryClient.resetQueries({ queryKey: diagramsKey(workspace.id) });
  }, [cursorStale, queryClient, workspace.id]);

  const create = async (values: { name: string; description: string | null }) => {
    const diagram = await commands.create(values);
    await refresh();
    return diagram;
  };

  // Копия — сразу, без диалога: «Имя (копия)», и сразу открывается (как в прототипе).
  const duplicate = async (diagram: ListedDiagram) => {
    setError(null);
    try {
      const copy = await commands.duplicate(diagram.id, {
        name: t('home.copyOf').replace('{name}', diagram.name).slice(0, 200),
        description: diagram.description,
      });
      await refresh();
      await navigate(paths.diagram(workspace.id, copy.id));
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="flex items-center justify-between px-6 py-4">
        <Brand to={paths.root} title={t('home.brandTitle')} wordmark />
        <UserMenu workspaceId={workspace.id} />
      </header>
      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-6 pt-6 pb-16">
        <ArchivedNotice />
        {can(workspace, 'assistant.use') && writable && (
          <ChatStart
            workspaceId={workspace.id}
            diagrams={items}
            canCreate={canCreate}
            loading={diagrams.isPending}
            createDiagram={(name) => create({ name, description: null })}
          />
        )}
        {diagrams.isError ? (
          <Notice tone="danger">{t(errorMessageKey(diagrams.error))}</Notice>
        ) : (
          <DiagramList
            workspaceId={workspace.id}
            diagrams={items}
            loading={diagrams.isPending}
            actions={{
              canCreate,
              canRename: can(workspace, 'diagram.edit') && writable,
              canDelete: can(workspace, 'diagram.delete') && writable,
              onCreate: () => setDialog({ kind: 'create' }),
              onRename: (diagram) => setDialog({ kind: 'rename', diagram }),
              onDuplicate: (diagram) => void duplicate(diagram),
              onDelete: (diagram) => setDialog({ kind: 'delete', diagram }),
            }}
          />
        )}
        {diagrams.hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="secondary"
              disabled={diagrams.isFetchingNextPage}
              onClick={() => void diagrams.fetchNextPage()}
            >
              {t('common.showMore')}
            </Button>
          </div>
        )}
        {error !== null && (
          <p className="mt-2 px-1 text-xs text-danger">{t(errorMessageKey(error))}</p>
        )}
      </main>

      <DiagramFormDialog
        open={dialog?.kind === 'create'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={t('home.newDiagram')}
        submitLabel={t('home.create')}
        initial={{ name: '', description: '' }}
        onSubmit={async (values) => {
          const diagram = await create(values);
          await navigate(paths.diagram(workspace.id, diagram.id));
        }}
      />
      <DiagramFormDialog
        open={dialog?.kind === 'rename'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={t('diagram.rename')}
        submitLabel={t('common.save')}
        initial={{
          name: dialog?.kind === 'rename' ? dialog.diagram.name : '',
          description: dialog?.kind === 'rename' ? (dialog.diagram.description ?? '') : '',
        }}
        onSubmit={async (values) => {
          if (dialog?.kind !== 'rename') return;
          await commands.update(dialog.diagram.id, values);
          await refresh();
        }}
      />
      <ConfirmDialog
        open={dialog?.kind === 'delete'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={t('diagram.deleteTitle')}
        description={
          dialog?.kind === 'delete'
            ? t('home.deleteMessage').replace('{name}', dialog.diagram.name)
            : ''
        }
        confirmLabel={t('common.delete')}
        danger
        pending={deleting}
        onConfirm={() => {
          if (dialog?.kind !== 'delete') return;
          setDeleting(true);
          setError(null);
          commands
            .remove(dialog.diagram.id)
            .then(refresh)
            .then(() => setDialog(null))
            .catch((caught: unknown) => {
              setError(caught);
              setDialog(null);
            })
            .finally(() => setDeleting(false));
        }}
      />
    </div>
  );
}
